import React, {
  type Context,
  createContext,
  useCallback,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import {
  Collapse,
  NavItem,
  Navbar,
  NavbarBrand,
  NavbarToggler,
} from "reactstrap";
import { AccountAccessRight } from "src/accounts/enums/AccountAccessRight";
import { AccountsLabel } from "src/accounts/formatters/AccountFormatters";
import { ACCOUNT_LIST_PATH } from "src/accounts/views/AccountsPaths";
import { ApiSubscribersAccessRight } from "src/api-subscribers/enums/ApiSubscribersAccessRight";
import { ApiSubscribersLabel } from "src/api-subscribers/formatters/ApiSubscribersFormatters";
import { API_SUBSCRIBERS_VIEW_PATH } from "src/api-subscribers/views/ApiSubscribersPaths";
import { LanguageSwitch } from "src/app/components/LanguageSwitch";
import { DEFAULT_VIEW_PATH } from "src/app/ui/DefaultViewPath";
import { LogoutLabel } from "src/authentication/AuthenticationFormatters";
import { useAccessible } from "src/authentication/hooks/useAccessible";
import { useAuthenticationToken } from "src/authentication/hooks/useAuthenticationToken";
import { useLogout } from "src/authentication/hooks/useLogout";
import { BillingsAccessRight } from "src/billings/enums/BillingsAccessRight";
import { BillingsLabel } from "src/billings/formatters/BillingsFormatters";
import { BILLINGS_VIEW_PATH } from "src/billings/views/BillingsPath";
import { CampaignsLabel } from "src/campaigns/CampaignFormatters";
import ChatsAccessRight from "src/chats/enums/ChatsAccessRight";
import { ChatsLabel } from "src/chats/formatters/ChatFormatters";
import { useChatMessages } from "src/chats/hooks/useChatMessages.hooks";
import { CHATS_PATH, getChatsStatusPath } from "src/chats/views/ChatPaths";
import { CouponGroupAccessRight } from "src/coupon-groups/enums/AccountAccessRight";
import { CouponGroupsLabel } from "src/coupon-groups/formatters/CouponGroupFormatters";
import { COUPON_GROUPS_VIEW_PATH } from "src/coupon-groups/views/CouponGroupPaths";
import { DashboardLabel } from "src/dashboard/DashboardFormatters";
import { DASHBOARD_VIEW_PATH } from "src/dashboard/views/DashboardPaths";
import {
  ActiveDispatchesLabel,
  DraftsDispatchesLabel,
  HistoryDispatchesLabel,
} from "src/dispatches/formatters/DispatchFormatters";
import "./menu.css";
import {
  DISPATCHES_VIEW_PATH,
  GetDispatchesViewPath,
} from "src/dispatches/views/DispatchPaths";
import { HumanoidAccessRight } from "src/humanoid/enums/HumanoidAccessRight";
import { HumanoidLabel } from "src/humanoid/HumanoidFormatters";
import { HUMANOID_VIEW_PATH } from "src/humanoid/views/HumanoidViewPaths";
import { ImportExportMenuItem } from "src/imports-exports/components/ImportExportMenuItem";
import IntegrationAccessRight from "src/integrations/enums/IntegrationAccessRight";
import { IntegrationsLabel } from "src/integrations/formatters/IntegrationFormatters";
import { INTEGRATIONS_LIST_PATH } from "src/integrations/views/IntegrationPaths";
import KeywordAccessRight from "src/keywords/enums/KeywordAccessRight";
import { KeywordLabel } from "src/keywords/formatters/KeywordFormatters";
import { KEYWORDS_VIEW_PATH } from "src/keywords/views/KeywordPaths";
import { ListAccessRight } from "src/lists/enums/ListAccessRight";
import { ListsLabel } from "src/lists/formatters/ListFormatters";
import { LISTS_VIEW_PATH } from "src/lists/views/ListsView";
import { LookupAccessRight } from "src/lookup/enums/LookupAccessRight";
import { LookupLabel } from "src/lookup/formatters/LookupFormatters";
import { LOOKUP_VIEW_PATH } from "src/lookup/views/LookupViewPaths";
import { ManageLabel } from "src/manage/ManageFormatters";
import { MANAGE_VIEW_PATH } from "src/manage/views/ManageView";
import { MerchantGroupAccessRight } from "src/merchantgroups/enums/MerchantGroupAccessRight";
import { MerchantGroupLabel } from "src/merchantgroups/formatters/MerchantGroupFormatters";
import { MERCHANT_GROUPS_VIEW_PATH } from "src/merchantgroups/views/MerchantGroupsView";
import { MerchantAccessRight } from "src/merchants/enums/MerchantAccessRight";
import { MerchantLabel } from "src/merchants/formatters/MerchantFormatters";
import { getMerchantsViewPath } from "src/merchants/views/MerchantPaths";
import OfferAccessRight from "src/offers/enums/OfferAccessRight";
import { OfferLabel } from "src/offers/formatters/OfferFormatters";
import { OFFERS_VIEW_PATH } from "src/offers/views/OfferPaths";
import SmartLinkAccessRight from "src/smartlinks/enums/SmartLinkAccessRight";
import { SmartLinkLabel } from "src/smartlinks/formatters/SmartLinkFormatters";
import { SMART_LINKS_VIEW_PATH } from "src/smartlinks/views/SmartLinksPath";
import StoreAccessRight from "src/stores/enums/StoreAccessRight";
import { StoreLabel } from "src/stores/formatters/StoreFormatters";
import { GetStoresViewPath } from "src/stores/views/StorePaths";
import SubscriberAccessRight from "src/subscribers/enums/SubscriberAccessRight";
import { SubscriberLabel } from "src/subscribers/formatters/SubscriberFormatters";
import { SUBSCRIBERS_VIEW_PATH } from "src/subscribers/views/SubscriberPaths";
import { UserAccessRight } from "src/users/enums/UserAccessRight";
import { UsersLabel } from "src/users/formatters/UserFormatters";
import { USERS_LIST_PATH } from "src/users/views/UsersPaths";
import { MenuItem } from "./MenuItem";
import { MenuNav } from "./MenuNav";

export const MenuCurrentPathContext: Context<string> = createContext("");

export const Menu = () => {
  const { pathname: currentPathName } = useLocation();
  const logout = useLogout();

  const [isOpen, setOpen] = useState(true);

  const onToggle = useCallback((): void => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const onLogout = useCallback((): void => {
    logout();
  }, [logout]);

  const authenticationToken = useAuthenticationToken();
  const { messages } = useChatMessages();

  return (
    <Navbar expand="md" color="light" light fixed={"top"}>
      <NavbarBrand to={DASHBOARD_VIEW_PATH} tag={Link}>
        Raange
      </NavbarBrand>
      <NavbarToggler onClick={onToggle} />

      <Collapse isOpen={isOpen} navbar>
        <MenuCurrentPathContext.Provider value={currentPathName}>
          <MenuNav align="center">
            <MenuItem
              pathName={DASHBOARD_VIEW_PATH}
              exact
              label={<DashboardLabel />}
            />
            {useAccessible(SubscriberAccessRight.VIEW_SUBSCRIBERS) && (
              <MenuItem
                pathName={SUBSCRIBERS_VIEW_PATH}
                label={<SubscriberLabel />}
              />
            )}
            <MenuItem
              pathName={DISPATCHES_VIEW_PATH}
              label={<CampaignsLabel />}
            >
              <MenuItem
                pathName={GetDispatchesViewPath("ACTIVE")}
                label={<ActiveDispatchesLabel />}
              />
              <MenuItem
                pathName={GetDispatchesViewPath("HISTORY")}
                label={<HistoryDispatchesLabel />}
              />
              <MenuItem
                pathName={GetDispatchesViewPath("DRAFT")}
                label={<DraftsDispatchesLabel />}
              />
              {useAccessible(HumanoidAccessRight.VIEW_HUMANOID) && (
                <MenuItem
                  pathName={HUMANOID_VIEW_PATH}
                  label={<HumanoidLabel />}
                />
              )}
              {useAccessible(LookupAccessRight.PERFORM_LOOKUP) && (
                <MenuItem pathName={LOOKUP_VIEW_PATH} label={<LookupLabel />} />
              )}
            </MenuItem>
            <MenuItem pathName={MANAGE_VIEW_PATH} label={<ManageLabel />}>
              {useAccessible(MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS) && (
                <MenuItem
                  pathName={MERCHANT_GROUPS_VIEW_PATH}
                  label={<MerchantGroupLabel />}
                />
              )}
              {useAccessible(
                MerchantAccessRight.VIEW_MERCHANTS,
                MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS,
              ) && (
                <MenuItem
                  pathName={getMerchantsViewPath(
                    authenticationToken.merchantGroupUUID,
                  )}
                  label={<MerchantLabel />}
                />
              )}
              {useAccessible(
                StoreAccessRight.VIEW_STORES,
                MerchantAccessRight.VIEW_MERCHANTS,
              ) && (
                <MenuItem
                  pathName={GetStoresViewPath(authenticationToken.merchantUUID)}
                  label={<StoreLabel />}
                />
              )}
              {useAccessible(
                OfferAccessRight.VIEW_OFFERS,
                MerchantAccessRight.VIEW_MERCHANTS,
              ) && (
                <MenuItem pathName={OFFERS_VIEW_PATH} label={<OfferLabel />} />
              )}
              {useAccessible(
                KeywordAccessRight.VIEW_KEYWORDS,
                MerchantAccessRight.VIEW_MERCHANTS,
              ) && (
                <MenuItem
                  pathName={KEYWORDS_VIEW_PATH}
                  label={<KeywordLabel />}
                />
              )}
              {useAccessible(
                CouponGroupAccessRight.VIEW_COUPON_GROUPS,
                MerchantAccessRight.VIEW_MERCHANTS,
              ) && (
                <MenuItem
                  pathName={COUPON_GROUPS_VIEW_PATH}
                  label={<CouponGroupsLabel />}
                />
              )}
              {useAccessible(SmartLinkAccessRight.VIEW_SMART_LINKS) && (
                <MenuItem
                  pathName={SMART_LINKS_VIEW_PATH}
                  label={<SmartLinkLabel />}
                />
              )}
              {useAccessible(
                ListAccessRight.VIEW_LISTS,
                MerchantAccessRight.VIEW_MERCHANTS,
              ) && (
                <MenuItem pathName={LISTS_VIEW_PATH} label={<ListsLabel />} />
              )}
              {useAccessible(UserAccessRight.VIEW_USERS) && (
                <MenuItem pathName={USERS_LIST_PATH} label={<UsersLabel />} />
              )}
              {useAccessible(IntegrationAccessRight.VIEW_INTEGRATIONS) && (
                <MenuItem
                  pathName={INTEGRATIONS_LIST_PATH}
                  label={<IntegrationsLabel />}
                />
              )}
              <ImportExportMenuItem />
              {useAccessible(AccountAccessRight.VIEW_ACCOUNTS) && (
                <MenuItem
                  pathName={ACCOUNT_LIST_PATH}
                  label={<AccountsLabel />}
                />
              )}
              {useAccessible(
                ApiSubscribersAccessRight.VIEW_API_SUBSCRIBERS,
              ) && (
                <MenuItem
                  pathName={API_SUBSCRIBERS_VIEW_PATH}
                  label={<ApiSubscribersLabel />}
                />
              )}
              {useAccessible(BillingsAccessRight.VIEW_BILLINGS) && (
                <MenuItem
                  pathName={BILLINGS_VIEW_PATH}
                  label={<BillingsLabel />}
                />
              )}
            </MenuItem>
            {useAccessible(ChatsAccessRight.VIEW_CHATS_LIST) && (
              <MenuItem
                pathName={CHATS_PATH}
                label={<ChatsLabel count={messages.length} />}
              >
                <MenuItem
                  pathName={getChatsStatusPath("OPEN")}
                  label={<FormattedMessage id="chats.live" />}
                />
                <MenuItem
                  pathName={getChatsStatusPath("COMPLETED")}
                  label={<FormattedMessage id="chats.completed" />}
                />
              </MenuItem>
            )}
          </MenuNav>
          <MenuNav align="right">
            <NavItem>
              <LanguageSwitch />
            </NavItem>
            <MenuItem
              pathName={DEFAULT_VIEW_PATH}
              onClick={onLogout}
              label={<LogoutLabel />}
              isActive={false}
            />
          </MenuNav>
        </MenuCurrentPathContext.Provider>
      </Collapse>
    </Navbar>
  );
};
